import { TokenLogo } from "./TokenSelect";
import { useEffect, useRef, useState, useCallback } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";
import { CandlestickChart, Search, Repeat2, ExternalLink } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fmtUsd, fmtPct, short } from "../lib/format";
import { addrUrl } from "../lib/chains";
import { Loading, ErrorState, Empty } from "./State";
import { PairSearch } from "./PairSearch";

const TFS = ["1m", "5m", "15m", "1h", "4h", "1d", "1w"];
const STABLES = ["USDG", "USDC", "USDT", "DAI"];

const precisionFor = (p) => (p >= 1000 ? 2 : p >= 1 ? 4 : Math.min(12, Math.max(4, Math.ceil(-Math.log10(p)) + 3)));

const Stat = ({ label, value, testid, tone }) => (
  <div className="min-w-[96px]">
    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</p>
    <p data-testid={testid} className={`text-sm font-semibold font-mono ${tone || "text-slate-100"}`}>{value}</p>
  </div>
);

function useCandles(pool, tf, side) {
  const [state, setState] = useState({ loading: true, candles: [], error: null });
  const load = useCallback(() => {
    if (!pool) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get(`/market/pools/${pool}/ohlcv`, { params: { tf, token: side } })
      .then((r) => setState({ loading: false, candles: r.data.candles, error: null }))
      .catch((e) => setState({ loading: false, candles: [], error: errMsg(e, "Chart data unavailable") }));
  }, [pool, tf, side]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  return { ...state, reload: load };
}

const ChartCanvas = ({ candles }) => {
  const el = useRef(null);
  useEffect(() => {
    if (!el.current || !candles.length) return;
    const chart = createChart(el.current, {
      autoSize: true,
      localization: { locale: "en-US" },
      layout: { background: { color: "transparent" }, textColor: "#94A3B8", fontFamily: "JetBrains Mono, monospace", fontSize: 11 },
      grid: { vertLines: { color: "rgba(57,255,20,0.05)" }, horzLines: { color: "rgba(57,255,20,0.05)" } },
      crosshair: { mode: CrosshairMode.Normal, vertLine: { color: "#39FF14", labelBackgroundColor: "#1A202C" }, horzLine: { color: "#39FF14", labelBackgroundColor: "#1A202C" } },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.08)" },
      timeScale: { borderColor: "rgba(255,255,255,0.08)", timeVisible: true },
    });
    const last = candles[candles.length - 1].close;
    const prec = precisionFor(last);
    const cs = chart.addCandlestickSeries({ upColor: "#39FF14", downColor: "#FF3366", borderVisible: false, wickUpColor: "#39FF14", wickDownColor: "#FF3366",
      priceFormat: { type: "price", precision: prec, minMove: 1 / 10 ** prec } });
    const vs = chart.addHistogramSeries({ priceFormat: { type: "volume" }, priceScaleId: "" });
    vs.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    cs.setData(candles.map(({ volume, ...c }) => c));
    vs.setData(candles.map((c) => ({ time: c.time, value: c.volume, color: c.close >= c.open ? "rgba(57,255,20,0.35)" : "rgba(255,51,102,0.35)" })));
    chart.timeScale().fitContent();
    return () => chart.remove();
  }, [candles]);
  return <div ref={el} data-testid="price-chart-canvas" className="h-full w-full" />;
};

export const PriceChart = ({ pool, onPoolChange, hiveConfigured }) => {
  const [tf, setTf] = useState("1h");
  const [info, setInfo] = useState(null);
  const [infoErr, setInfoErr] = useState(null);
  const [side, setSide] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    setInfo(null); setInfoErr(null); setSide(null);
    api.get(`/market/pools/${pool}`).then((r) => {
      setInfo(r.data);
      setSide(STABLES.includes(r.data.base_token?.symbol) ? "quote" : "base");
    }).catch((e) => setInfoErr(errMsg(e)));
  }, [pool]);

  const { loading, candles, error, reload } = useCandles(side ? pool : null, tf, side || "base");
  const tok = info && (side === "quote" ? info.quote_token : info.base_token);
  const other = info && (side === "quote" ? info.base_token : info.quote_token);
  const price = info && (side === "quote" ? info.quote_price_usd : info.price_usd);
  const rangeChg = candles.length > 1 ? ((candles[candles.length - 1].close - candles[0].open) / candles[0].open) * 100 : null;

  return (
    <section data-testid="price-chart-panel" className="glass rounded-xl overflow-hidden circuit-border">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3 px-5 pt-4">
        <button data-testid="chart-pair-search-button" onClick={() => setSearchOpen(true)} className="flex items-center gap-3 group">
          <TokenLogo t={tok} size={36} />
          <div className="text-left">
            <p className="font-display text-lg font-bold text-white flex items-center gap-2" data-testid="chart-pair-name">
              {tok ? `${tok.symbol} / ${side === "quote" ? "USD" : "USD"}` : "Loading pair"} <Search className="h-4 w-4 text-slate-500 group-hover:text-lime" />
            </p>
            <p className="text-[11px] font-mono text-slate-500">{info ? `${info.name} · ${info.protocol}` : short(pool)}</p>
          </div>
        </button>
        {info && (
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Stat label="Price" value={fmtUsd(price, false)} testid="chart-stat-price" />
            <Stat label={side === "base" ? "24h change" : "Loaded range Δ"} value={fmtPct(side === "base" ? info.change_24h_pct : rangeChg)} testid="chart-stat-change"
              tone={(side === "base" ? info.change_24h_pct : rangeChg) >= 0 ? "text-lime" : "text-danger"} />
            <Stat label="24h volume" value={fmtUsd(info.volume_24h_usd)} testid="chart-stat-volume" />
            <Stat label="Pool liquidity" value={fmtUsd(info.liquidity_usd)} testid="chart-stat-liquidity" />
            <Stat label={side === "base" ? "Market cap" : "Pool FDV"} value={side === "base" ? fmtUsd(info.market_cap_usd ?? null) : "n/a for quote"} testid="chart-stat-mcap" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          {info && (
            <button data-testid="chart-invert-button" onClick={() => setSide(side === "base" ? "quote" : "base")} title={`Chart ${other?.symbol}`}
              className="flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] text-slate-400 hover:text-lime hover:border-lime/40">
              <Repeat2 className="h-3.5 w-3.5" /> {other?.symbol}
            </button>
          )}
          <a href={addrUrl(pool)} target="_blank" rel="noreferrer" data-testid="chart-pool-explorer-link" className="text-slate-500 hover:text-lime"><ExternalLink className="h-4 w-4" /></a>
        </div>
      </div>
      {!hiveConfigured && (
        <p data-testid="chart-hive-notice" className="mx-5 mt-3 text-[11px] text-warning/90 font-mono">
          HIVE token contract not configured yet — showing a live Uniswap v3 pool instead. No HIVEVORA price is shown until a real HIVEVORA pool exists.
        </p>
      )}
      <div className="flex items-center gap-1 px-5 pt-3">
        <CandlestickChart className="h-4 w-4 text-lime mr-1" />
        {TFS.map((t) => (
          <button key={t} data-testid={`chart-timeframe-${t}`} onClick={() => setTf(t)}
            className={`rounded px-2.5 py-1 text-[11px] font-mono font-semibold transition-colors ${tf === t ? "bg-lime text-black" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>{t}</button>
        ))}
      </div>
      <div className="h-[340px] lg:h-[380px] px-2 pb-2">
        {infoErr ? <ErrorState message={infoErr} testid="chart-error" />
          : loading && !candles.length ? <Loading label="Loading candles from indexed swaps" testid="chart-loading" />
          : error ? <ErrorState message={error} onRetry={reload} testid="chart-error" />
          : !candles.length ? <Empty title="No trades in this timeframe" hint="No candles are fabricated. Try a longer timeframe." testid="chart-empty" />
          : <ChartCanvas candles={candles} />}
      </div>
      <p className="px-5 pb-3 text-[10px] font-mono text-slate-600">Source: GeckoTerminal OHLCV from on-chain swap events · USD · refreshes every 30s</p>
      <PairSearch open={searchOpen} onOpenChange={setSearchOpen} onSelect={(p) => { onPoolChange(p.address); setSearchOpen(false); }} />
    </section>
  );
};
